import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, ButtonGroup, Paper, Typography } from "@mui/material";

import { Translate } from "@/components/Application/Translate";
import { NodeAttachedDocumentInformation } from "@/components/Procedures/EditNode/NodeAttachedDocumentInformation";
import NodeInformation from "@/components/Procedures/EditNode/NodeInformation";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";

import { NodeEditSection } from "@/model/Procedure/EditNode";
import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";
import { DeleteProcedureNodeRequest } from "@/model/Procedure/ProcedureNodeDependency";

import { DeleteProcedureNode } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

function parseNodeEditSection(value: string | undefined): NodeEditSection {
	switch (value) {
		case "dependencies":
			return "dependencies";
		case "document":
			return "document";
		case "document-upload":
			return "document-upload";
		case "node":
			return "node";
		default:
			return "";
	}
}

export interface NodeViewProps {
	node: ProcedureDetailNode | undefined;
	edit: boolean;
	toggleEditNode: (editNode: boolean) => void;
}

export const NodeView: FC<NodeViewProps> = (props) => {
	const { id, nodeEditSection } = useParams<RouteParams>();
	const procedureId = parseInt(id);
	const [editSection, setEditSection] = useState<NodeEditSection>(
		parseNodeEditSection(nodeEditSection),
	);
	const deleteNode = DeleteProcedureNode();

	useEffect(() => {
		setEditSection(parseNodeEditSection(nodeEditSection));
	}, [props.node?.SchemeId, nodeEditSection]);

	const deleteAndRedirect = (request: DeleteProcedureNodeRequest) => {
		deleteNode(request);
	};

	const handleDelete = () => {
		const request: DeleteProcedureNodeRequest = {
			ProcedureId: procedureId,
			SchemeId: props.node?.SchemeId ?? 0,
			Checklists: props.node?.Children.map((n) => n.CheckListId) ?? [],
		};
		try {
			AlertRequest.confirm(deleteAndRedirect, request, props.node?.Name);
		} catch (err) {
			AlertRequest.failed();
		}
	};

	if (!props.edit) {
		return null;
	}

	return (
		<Paper
			sx={{
				padding: 2,
				margin: "0 auto",
				position: "relative",
				display: "flex",
				flexDirection: "column",
				flexGrow: 1,
				background: "rgb(232, 236, 244)",
				width: "100%",
				maxWidth: "800px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
					paddingRight: 3,
					paddingLeft: 3,
				}}
			>
				<Typography variant="h4">
					<Translate String="Procedure.Node.Header">Edit Node</Translate>
				</Typography>
				<ButtonGroup variant="outlined" style={{ background: "white" }}>
					<Button color="error" onClick={handleDelete}>
						<DeleteIcon />
					</Button>
					<Button color="primary" onClick={() => props.toggleEditNode(false)}>
						<CloseOutlinedIcon />
					</Button>
				</ButtonGroup>
			</Box>
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 2,
					margin: "0 1rem",
				}}
			>
				<Box
					sx={{
						flex: "1 1 45%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<NodeInformation
						procedureId={procedureId}
						node={props.node}
						editSection={editSection}
					/>
				</Box>
				<Box
					sx={{
						flex: "1 1 45%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<NodeAttachedDocumentInformation
						procedureId={procedureId}
						node={props.node}
						editSection={editSection}
					/>
				</Box>
			</Box>
		</Paper>
	);
};
